import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Theme, Text, MessageCard } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import { styled } from '@mui/styles';

import CriticalIllness from './Benefits/CriticalIllness.js';
import DeathDismemberment from './Benefits/DeathDismemberment.js';
import Dental from './Benefits/Dental.js';
import DependentLife from './Benefits/DependentLife.js';
import Drugs from './Benefits/Drugs.js';
import Efap from './Benefits/Efap.js';
import LifeInsurance from './Benefits/LifeInsurance.js';
import Ltd from './Benefits/Ltd.js';
import MajorMedical from './Benefits/MajorMedical.js';
import OutOfCountry from './Benefits/OutOfCountry.js';
import Paramedicals from './Benefits/Paramedicals.js';
import SpendingAccounts from './Benefits/SpendingAccounts.js';
import Std from './Benefits/Std.js';
import Vision from './Benefits/Vision.js';
import VirtualHealthcare from './Benefits/VirtualHealthcare.js';
import permissionLevel from '../../../services/permissions.js';
import FinancialAwareness from './Benefits/FinancialAwareness.js';

const StyledColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column'
    }
}));

const StyledLeft = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: '50%',
    maxWidth: '50%',
    [theme.breakpoints.up('md')]: {
        minWidth: '50%'
    },
    [theme.breakpoints.down('md')]: {
        minWidth: '100%',
        maxWidth: '100%'
    }
}));

const StyledRight = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minWidth: '50%',
    maxWidth: '50%',
    [theme.breakpoints.up('md')]: {
        minWidth: '50%'
    },
    [theme.breakpoints.down('md')]: {
        minWidth: '100%',
        maxWidth: '100%'
    }
}));

class Coverage extends PureComponent {
    state = {
        expanded: []
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Coverage', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    _handleChange = (panel) => (event, expanding) => {
        let expanded = [ ...this.state.expanded ];
        const index = expanded.indexOf(panel);

        if (expanding && index === -1) {
            expanded.push(panel);
        } else {
            expanded.splice(index, 1);
        }

        this.setState({
            expanded
        });
    };

    _defineDeductibleVerbiage = (coverage, perClaim = false) => {
        switch (this.props.meta.coverageType) {
            case 'Solo':
                return coverage.soloDeductibleAmount ?
                    <Text small margin="5px 0px 10px 0px">{ `$${ helpers.formatNumber(coverage.soloDeductibleAmount) } Deductible per ${ perClaim ? 'claim' : 'year' }.` }</Text>
                    : null;
            case 'Duo':
                return coverage.duoDeductibleAmount ?
                    <Text small margin="5px 0px 10px 0px">{ `$${ helpers.formatNumber(coverage.duoDeductibleAmount) } Deductible per ${ perClaim ? 'claim' : 'year' }.` }</Text>
                    : null;
            case 'Family':
                return coverage.familyDeductibleAmount ?
                    <Text small margin="5px 0px 10px 0px">{ `$${ helpers.formatNumber(coverage.familyDeductibleAmount) } Deductible per ${ perClaim ? 'claim' : 'year' }.` }</Text>
                    : null;
            default:
                return null;
        }
    };

    render() {
        // Notice: PlanDesign is currently only being passed in by member dropdowns, otherwise null
        const { meta, planDesign = null, disableBorder = false } = this.props;
        const { expanded } = this.state;
        const isDependent = meta.isDependent;
        let waivedEHC = false;
        let waivedDental = false;
        let showWaiveForHealthDependentWarning = false;
        let showWaiveForDentalDependentWarning = false;
        const isStudentGroup = meta.isStudentGroup;

        const styles = {
            contentBlock: {
                backgroundColor: Theme.colors.surface,
                boxShadow: disableBorder ? null : '0 1px 2px rgba(0, 0, 0, .15)',
                display: 'block',
                margin: 'auto auto 30px auto',
                padding: '30px',
                position: 'relative',
                zIndex: 3
            },
            block: {
                flex: 1,
                padding: 10
            },
            panelDetails: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: '0 20px 0 0'
            },
            expanded: {
                "&$expanded": {
                    '&>:last-child': {
                        color: `${ Theme.colors.mainTextGrey }`,
                        fontSize: 16,
                        fontWeight: '500 !important',
                        marginRight: '-12px'
                    },
                    minHeight: '24px',
                    margin: '0'
                }
            },
            panelContent: {
                color: Theme.colors.mainTextGrey,
                paddingLeft: 0,
                paddingRight: 0,
                margin: 0
            },
            root: {
                paddingLeft: 0,
                paddingRight: 0,
                minHeight: '50px !important',
                borderBottomWidth: 1,
                borderBottomColor: Theme.colors.lightGrey,
                borderBottomStyle: 'solid'
            },
            panelSpare: {
                height: 48,
                cursor: 'default',
                backgroundColor: Theme.colors.surface
            },
            row: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            },
            title: {
                flex: 1
            },
            spendingRow: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: '50%',
                maxWidth: '50%'
            },
            accordionSummary: {
                "& .MuiAccordionSummary-expandIconWrapper": {
                    color: Theme.colors.mainTextGrey,
                    padding: "12px"

                },
                '& .MuiAccordionSummary-expandIconWrapper:hover': {
                    backgroundColor: "#bfbfbf",
                    borderRadius: "50%"
                },
                '&.MuiAccordionSummary-root': {
                    borderColor: "#e6e7e8",
                    borderBottomStyle: "solid",
                    borderBottomWidth: "1px"
                },
                padding: "0px",
                maxHeight: "70px"
            }
        };

        if (meta.hasWaived && meta.hasWaived.includes('Health')) {
            waivedEHC = !meta.waiveFor.includes('Self') ? isDependent : true;
            showWaiveForHealthDependentWarning = true;
        }

        if (meta.hasWaived && meta.hasWaived.includes('Dental')) {
            waivedDental = !meta.waiveFor.includes('Self') ? isDependent : true;
            showWaiveForDentalDependentWarning = true;
        }

        const coverages = [];
        const left = [];
        const right = [];

        const {
            lifeInsurance,
            deathDismemberment,
            dental,
            drugs,
            majorMedical,
            paramedicals,
            vision,
            efap,
            dependentLife,
            ltd,
            std,
            spendingAccounts,
            criticalIllness,
            outOfCountry,
            virtualHealthcare,
            financialAwareness
        } = this.props.coverage;

        if (!isDependent && lifeInsurance?.benefitActive === 1) {
            coverages.push(<LifeInsurance
                key="lifeInsurance"
                classes={ styles }
                expanded={ expanded }
                lifeInsurance={ lifeInsurance }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && dependentLife?.benefitActive === 1) {
            coverages.push(<DependentLife
                key="dependentLife"
                classes={ styles }
                expanded={ expanded }
                dependentLife={ dependentLife }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && std?.benefitActive === 1) {
            coverages.push(<Std
                key="std"
                classes={ styles }
                expanded={ expanded }
                std={ std }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (dental?.benefitActive === 1) {
            coverages.push(<Dental
                key="dental"
                classes={ styles }
                expanded={ expanded }
                dental={ dental }
                handleChange={ this._handleChange }
                defineDeductibleVerbiage={ this._defineDeductibleVerbiage }
                showDependentWarning={ showWaiveForDentalDependentWarning }
                waivedDental={ waivedDental }
                isStudent={isStudentGroup}
            />);
        }

        if (paramedicals?.benefitActive === 1 &&
            (
                paramedicals.reimbursement !== 0 ||
                (paramedicals.hasMentalWellnessGrouped !== 0 && paramedicals.mentalWellnessReimbursement !== 0) ||
                (paramedicals.hasFunctionalTreatmentGrouped !== 0 && paramedicals.functionalTreatmentReimbursement !== 0) ||
                paramedicals.mentalWellnessMaximum !== 0
            )
        ) {
            coverages.push(<Paramedicals
                key="paramedicals"
                classes={ styles }
                expanded={ expanded }
                paramedicals={ paramedicals }
                handleChange={ this._handleChange }
                defineDeductibleVerbiage={ () => this._defineDeductibleVerbiage(majorMedical) }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                hasDeductible={ majorMedical.hasDeductible}
                isStudent={isStudentGroup}
            />);
        }

        if (vision?.benefitActive === 1) {
            coverages.push(<Vision
                key="vision"
                classes={ styles }
                expanded={ expanded }
                vision={ vision }
                handleChange={ this._handleChange }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && efap?.benefitActive === 1) {
            coverages.push(<Efap
                key="efap"
                classes={ styles }
                expanded={ expanded }
                efap={ efap }
                handleChange={ this._handleChange }
                theme={ Theme }
                waivedEHC={ waivedEHC }
                isStudent={isStudentGroup}
                accessCode={ planDesign ? planDesign.efap.accessCode : null }
            />);
        }
        
        if (!isDependent && virtualHealthcare?.benefitActive === 1) {
            coverages.push(<VirtualHealthcare
                key="virtualHealthcare"
                classes={ styles }
                expanded={ expanded }
                virtualHealthcare={ virtualHealthcare }
                handleChange={ this._handleChange }
                theme={ Theme }
                isStudent={isStudentGroup}
                accessCode={ planDesign ? planDesign.virtualHealthcare.accessCode : null }
            />);
        }

        if (!isDependent && financialAwareness?.benefitActive === 1) {
            coverages.push(<FinancialAwareness
                key="financialAwareness"
                classes={ styles }
                expanded={ expanded }
                financialAwareness={ financialAwareness }
                handleChange={ this._handleChange }
                theme={ Theme }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && deathDismemberment?.benefitActive === 1) {
            coverages.push(<DeathDismemberment
                key="deathDismemberment"
                classes={ styles }
                expanded={ expanded }
                deathDismemberment={ deathDismemberment }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && ltd?.benefitActive === 1) {
            coverages.push(<Ltd
                key="ltd"
                classes={ styles }
                expanded={ expanded }
                ltd={ ltd }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && criticalIllness?.benefitActive === 1) {
            coverages.push(<CriticalIllness
                key="criticalIllness"
                classes={ styles }
                expanded={ expanded }
                criticalIllness={ criticalIllness }
                handleChange={ this._handleChange }
                isStudent={isStudentGroup}
            />);
        }

        if (drugs?.benefitActive === 1) {
            coverages.push(<Drugs
                key="drugs"
                classes={ styles }
                expanded={ expanded }
                drugs={ drugs }
                handleChange={ this._handleChange }
                defineDeductibleVerbiage={ this._defineDeductibleVerbiage }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                isStudent={isStudentGroup}
            />);
        }

        if (majorMedical?.benefitActive === 1) {
            coverages.push(<MajorMedical
                key="majorMedical"
                classes={ styles }
                expanded={ expanded }
                majorMedical={ majorMedical }
                handleChange={ this._handleChange }
                defineDeductibleVerbiage={ this._defineDeductibleVerbiage }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent && outOfCountry?.benefitActive === 1) {
            coverages.push(<OutOfCountry
                key="outOfCountry"
                classes={ styles }
                expanded={ expanded }
                outOfCountry={ outOfCountry }
                handleChange={ this._handleChange }
                theme={ Theme }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                isStudent={isStudentGroup}
            />);
        }

        if (!isDependent
            && (spendingAccounts.health.hasBenefit === 1 || spendingAccounts.lifestyle.hasBenefit === 1)
        ) {
            coverages.push(<SpendingAccounts
                key="defineSpendingAccountType"
                classes={ styles }
                expanded={ expanded }
                spendingAccounts={ spendingAccounts }
                handleChange={ this._handleChange }
                defineSpendingAccountType={ this._defineSpendingAccountType }
                lsaClaimTypes={ this.props.lsaClaimTypes }
                isStudent={isStudentGroup}
            />);
        }

        coverages.map((section, index) => {
            index % 2 === 0 ? left.push(section) : right.push(section);
            return null;
        });

        return (
            <div>
                {
                    meta.hasWaived && (meta.hasWaived.includes('Health') || meta.hasWaived.includes('Dental')) &&
                        <Text fullWidth align="center" color="red">
                            The member Has Waived
                            {
                                meta.hasWaived.includes('Health') && meta.hasWaived.includes('Dental') ?
                                    ' Extended Health and Dental '
                                    :
                                    meta.hasWaived.includes('Health') ? ' Extended Health ' : ' Dental '
                            }
                            For
                            { meta.waiveFor.includes('Self') ? ' Self and Dependents' : ' Dependents' }
                        </Text>
                }

                {
                    left.length > 0 ?
                        <div style={ this.props.isAdjudicationView ? null : styles.contentBlock }>
                            <StyledColumn>
                                <StyledLeft>
                                    {
                                        left.map((section) => section)
                                    }
                                </StyledLeft>

                                <StyledRight>
                                    {
                                        right.map((section) => section)
                                    }
                                </StyledRight>
                            </StyledColumn>
                        </div>
                        :
                        <MessageCard
                            title="No Coverage"
                            subtitle="This user has no coverage."
                            small
                        />
                }

                <span id="user-coverage" />
            </div>
        );
    }
}

Coverage.propTypes = {
    meta: PropTypes.object,
    coverage: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    accessType: state.master.access
});

export default (connect(mapStateToProps)(Coverage));